import { useState } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopProductDetail } from "./desktop/product-detail.tsx";
import { readPurchaseAs, readViewAs, writeIdentity } from "../lib/identity.ts";
import { SubPage } from "../components/layout.tsx";
import { Button, ErrorBlock, Img, Price, QtyStepper, Spinner, fen } from "../components/ui.tsx";
import { useAddToCart, useMe, useProduct } from "../api/queries.ts";

/** 🔴 手机版和网页版是两套页面 —— 见 `lib/viewport.ts` 的说明。 */
function ProductDetailRoute() {
  return useIsDesktop() ? <DesktopProductDetail /> : <ProductDetail />;
}

function ProductDetail() {
  const { id } = useParams({ from: "/products/$id" });
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data, isPending, error, refetch } = useProduct(id);
  const addToCart = useAddToCart();
  /** 加购成功的短暂反馈 —— 不跳走的话必须给个回应，否则他不知道点上了没有。 */
  const [added, setAdded] = useState(false);

  const [skuId, setSkuId] = useState<number | null>(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  if (isPending) return <SubPage title="商品详情"><Spinner /></SubPage>;
  if (error) {
    return (
      <SubPage title="商品详情">
        <ErrorBlock error={error} onRetry={() => refetch()} />
      </SubPage>
    );
  }

  const sku = data.skus.find((s) => s.id === skuId) ?? data.skus[0];
  const canBuy = sku && sku.available;

  /**
   * 🔴 **「立即下单」不经过购物车。** 直接把这一件带到结算页 ——
   *    先加进购物车再结算的话，购物车里上次没买完的东西会**一起被下单**，
   *    而用户点的是"立即下单"，完全没预期。
   */
  const onBuyNow = () => {
    if (!me) {
      navigate({ to: "/login", search: { redirect: `/products/${id}` } });
      return;
    }
    if (!sku) return;
    navigate({ to: "/checkout", search: { skuId: sku.id, qty } });
  };

  const onAdd = async () => {
    if (!me) {
      navigate({ to: "/login", search: { redirect: `/products/${id}` } });
      return;
    }
    if (!sku) return;
    await addToCart.mutateAsync({ skuId: sku.id, quantity: qty });
    /**
     * ⚠️ **加完留在原地，不跳购物车。**
     *    建材一次要买好几样（砖、地板、灯、洁具），每加一件就被弹走，
     *    他得再返回来找刚才那一页 —— 加三件要来回六次。
     *    想去购物车的人点顶上那个图标就行，那儿有数量角标。
     */
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  /**
 * 🔴 **预览「我卖的价」时不能下单。**
 *    那个价是客户看到的，按它下单会生成一张"自己卖给自己"的单，
 *    而且跟服务端按真实身份算出的价对不上，会被 `PRICE_CHANGED` 拦下。
 *    与其让他点了报错，不如在按钮上就说清楚。
 */
const previewing = readPurchaseAs() !== null && readViewAs() === "shop";
  /** ⚠️ 切回「我自己」——「我的」页那两个胶囊之外的第二个入口。 */
  const backToMyself = () => writeIdentity("");

  return (
    <SubPage
      title={data.title}
      footer={
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-brand-600">
              <Price cents={sku?.price ?? data.fromPrice} className="text-lg" />
              {sku && <span className="ml-1 text-xs text-ink-500">/{sku.unit}</span>}
            </p>
            {sku && !sku.available && <p className="text-xs text-red-600">该规格暂时缺货</p>}
          </div>
          {/*
            🔴 **预览态不给死按钮，给一条出路。**
               店铺视角看到的是**客户**的价，按那个价下单会生成一张"自己卖给自己"
               的单，而且跟服务端按真实身份算的价对不上。但只是把按钮禁掉的话，
               用户就卡在这儿了 —— 他想买，而"要先切身份"这件事没人告诉他。
               点一下直接切回「我自己」（按进货价），他接着买就是了。
          */}
          {previewing ? (
            <Button size="lg" onClick={backToMyself}>
              按我的进货价买
            </Button>
          ) : (
            /*
              🔴 **两个按钮，不是一个。**
                 「加入购物车」是继续逛（建材一次要买好几样：砖、地板、灯、洁具），
                 「立即下单」是这一件就够了。以前只有一个按钮而且加完直接跳购物车，
                 等于把"继续逛"这条路堵死了。
              ⚠️ 主次要分开：立即下单是实心主按钮，加购是描边 ——
                 两个都做成主按钮的话，用户得先读完字才知道该点哪个。
            */
            <div className="flex shrink-0 gap-2">
              <Button
                size="lg"
                variant="outline"
                onClick={onAdd}
                disabled={!canBuy}
                loading={addToCart.isPending}
              >
                {added ? "已加入 ✓" : canBuy ? "加入购物车" : "缺货"}
              </Button>
              <Button size="lg" onClick={onBuyNow} disabled={!canBuy}>
                立即下单
              </Button>
            </div>
          )}
        </div>
      }
    >
      <Img src={data.imageUrls[imgIdx]} alt={data.title} className="aspect-square w-full object-cover" />

      {data.imageUrls.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2">
          {data.imageUrls.map((url, i) => (
            <button
              key={url}
              onClick={() => setImgIdx(i)}
              className={`size-14 shrink-0 overflow-hidden rounded border-2 ${
                i === imgIdx ? "border-brand-500" : "border-transparent"
              }`}
            >
              <img src={url} alt="" className="size-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <section className="bg-white px-4 py-3">
        <h1 className="text-[17px] font-medium leading-snug">{data.title}</h1>
        {data.subtitle && <p className="mt-1 text-sm text-ink-500">{data.subtitle}</p>}
        <p className="mt-2 text-brand-600">
          <Price cents={sku?.price ?? data.fromPrice} className="text-xl" />
          {sku?.listPrice && sku.listPrice > sku.price && (
            <span className="ml-2 text-sm text-ink-500 line-through">{fen(sku.listPrice)}</span>
          )}
        </p>
      </section>

      {data.skus.length > 0 && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 text-sm font-medium">规格</h2>
          <div className="flex flex-wrap gap-2">
            {data.skus.map((s) => {
              const label = Object.values(s.specs).join(" · ") || "默认";
              const selected = s.id === sku?.id;
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    setSkuId(s.id);
                    setQty(1);
                  }}
                  disabled={!s.available}
                  className={`rounded-lg border px-3 py-2 text-sm ${
                    selected
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : s.available
                        ? "border-neutral-300 text-ink-900"
                        : "border-neutral-200 text-neutral-400 line-through"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-ink-500">数量</span>
            <QtyStepper value={qty} onChange={setQty} max={sku?.stock ?? 9999} />
          </div>
          {sku && (
            <p className="mt-1 text-right text-xs text-ink-500">库存 {sku.stock} {sku.unit}</p>
          )}
        </section>
      )}

      {Object.keys(data.attributes).length > 0 && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 text-sm font-medium">参数</h2>
          <dl className="divide-y divide-neutral-100 text-sm">
            {Object.entries(data.attributes).map(([k, v]) => (
              <div key={k} className="flex justify-between py-2">
                <dt className="text-ink-500">{k}</dt>
                <dd className="text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {data.installNote && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 text-sm font-medium">安装说明</h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-500">{data.installNote}</p>
        </section>
      )}

      {data.descMd && (
        <section className="mt-2 bg-white px-4 py-3">
          <h2 className="mb-2 text-sm font-medium">详情</h2>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-500">{data.descMd}</p>
        </section>
      )}

      {/*
        ── 商品详情图（淘宝那条长图）──
        🔴 **顶部轮播放 `imageUrls`（商品长什么样），这里放 `detailImageUrls`
           （介绍商品）**，两组不能混：混了的话说明长图会跑进轮播和订单缩略图，
           缩成方块什么都看不清。
        ⚠️ **不设高度、不裁剪**：详情图是 800×3000 这种长图，
           `aspect-square object-cover` 会把中间的字全切掉。
        ⚠️ 用原生 `<img>` 而不是 `Img`：`Img` 是按缩略图设计的（占位方块 + 兜底图），
           长图套上去会先撑出一个方形空洞再跳变。
      */}
      {data.detailImageUrls.length > 0 && (
        <section className="mt-2 bg-white">
          <h2 className="px-4 pt-3 pb-2 text-sm font-medium">商品详情</h2>
          <div className="flex flex-col">
            {data.detailImageUrls.map((url, i) => (
              <img key={url} src={url} alt={`详情图 ${i + 1}`} loading="lazy" className="w-full" />
            ))}
          </div>
        </section>
      )}
    </SubPage>
  );
}

export const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/products/$id",
  component: ProductDetailRoute,
});
